import { Button } from '@talentmesh/ui/components/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@talentmesh/ui/components/tooltip';
import { TypographyH4, TypographyP } from '@talentmesh/ui/components/typography';
import { cn } from '@talentmesh/ui/lib/utils';
import { HelpCircle } from 'lucide-react';
import { getColorByTestValidity, getTextByTestValidity } from './TestValidityUtils';
import UIStrings from '@/Utils/UIStrings';

interface TestValidityScore {
    scoreName: string;
    descritpion: string;
}

const testValidityScores: Array<TestValidityScore> = [
    { scoreName: UIStrings.TestValidityVeryGood, descritpion: UIStrings.TestResultsCanBeReliedOn },
    { scoreName: UIStrings.TestValidityGood, descritpion: UIStrings.TestResultsCanBeReliedOn },
    { scoreName: UIStrings.TestValidityModerate, descritpion: UIStrings.UseWithCaution },
    { scoreName: UIStrings.TestValidityPoor, descritpion: UIStrings.ImpressionManagement },
    { scoreName: UIStrings.TestValidityVeryPoor, descritpion: UIStrings.ImpressionManagement },
];

interface TestValidityProps {
    value: number;
}

function TestValidity({ value }: TestValidityProps) {
    return (
        <div className="flex w-full flex-col items-center justify-center">
            <div className="flex items-center justify-center gap-1">
                <TypographyP className="text-foreground">{UIStrings.TestValidity}</TypographyP>
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon-xs"
                                aria-label={UIStrings.TestValidityScore}
                            >
                                <HelpCircle className="size-5" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent
                            side="bottom"
                            align="end"
                            sideOffset={8}
                            className="bg-card text-foreground border-border max-w-md border p-0 drop-shadow"
                            arrowProps={{ className: 'border-b border-r border-border bg-popover fill-popover' }}
                        >
                            <div className="flex min-w-56 flex-col items-start justify-center gap-4 p-4">
                                <TypographyH4 className="text-foreground">{UIStrings.TestValidityScore}</TypographyH4>
                                <TypographyP className="text-foreground">{UIStrings.HighTestValidityScore}</TypographyP>
                                <div className="bg-accent w-full rounded-xl p-2">
                                    <div className="flex flex-col gap-2">
                                        {testValidityScores.map((score) => (
                                            <div key={score.scoreName}>
                                                <span className="text-primary text-sm font-bold">{`${score.scoreName} - `}</span>
                                                <span className="text-primary text-sm">{score.descritpion}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>
            <TypographyP className={cn('font-bold', getColorByTestValidity(value))}>
                {getTextByTestValidity(value)}
            </TypographyP>
        </div>
    );
}

export default TestValidity;
